/**
 * Owns the session for the whole app.
 *
 * Three jobs:
 *
 *   1. **Resume.** On a cold load it exchanges the httpOnly refresh cookie for
 *      an access token, so a reload does not sign anyone out. Until that answer
 *      arrives the status is `loading` and the guards render a spinner rather
 *      than bouncing an authenticated user to the login screen.
 *   2. **Stay resumed.** It refreshes a minute before the access token expires,
 *      so a session that is being used never lapses mid-click.
 *   3. **Stay honest.** It mirrors the client's own session events, so React
 *      state can never claim a session the token store has already dropped.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { LoginRequest, SessionUser } from '@wend/shared';

import { login, logout } from '../lib/api/auth';
import { onSessionChange, refreshSession } from '../lib/api/client';
import { AuthContext, type AuthContextValue, type AuthStatus } from './auth-context';

/** Refresh this far before the access token expires. */
const REFRESH_LEAD_MS = 60_000;
/** Never schedule a refresh tighter than this, however short the TTL is. */
const MIN_REFRESH_DELAY_MS = 5_000;

interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
  /** Epoch milliseconds at which the access token expires. */
  expiresAt: number | null;
}

const SIGNED_OUT: AuthState = { status: 'unauthenticated', user: null, expiresAt: null };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: 'loading',
    user: null,
    expiresAt: null,
  });

  /* -- mirror the client's session, whoever changed it -------------------- */
  useEffect(() => {
    return onSessionChange((session) => {
      setState(
        session === null
          ? SIGNED_OUT
          : {
              status: 'authenticated',
              user: session.user,
              expiresAt: Date.now() + session.expiresIn * 1000,
            },
      );
    });
  }, []);

  /* -- resume an existing session on first load --------------------------- */
  const resumed = useRef(false);

  useEffect(() => {
    // StrictMode runs this effect twice in development. `refreshSession` is
    // single-flight so a second call would join the first rather than replay a
    // rotated token, but not asking twice is clearer.
    if (resumed.current) return;
    resumed.current = true;

    void refreshSession()
      .then((session) => {
        // A successful refresh already published the session; `null` means
        // there was simply no cookie to resume from.
        if (session === null) setState(SIGNED_OUT);
      })
      .catch(() => {
        // The API is unreachable. Show the login screen — the sign-in attempt
        // will surface the real network error rather than a boot-time blank.
        setState(SIGNED_OUT);
      });
  }, []);

  /* -- keep the access token fresh while the session is in use ------------ */
  useEffect(() => {
    if (state.status !== 'authenticated' || state.expiresAt === null) return;

    const delay = Math.max(MIN_REFRESH_DELAY_MS, state.expiresAt - Date.now() - REFRESH_LEAD_MS);

    const timer = window.setTimeout(() => {
      // A failure here is not fatal: the next API call gets a 401, refreshes
      // again, and only then decides whether the session is really gone.
      void refreshSession().catch(() => undefined);
    }, delay);

    return () => {
      window.clearTimeout(timer);
    };
  }, [state.status, state.expiresAt]);

  const signIn = useCallback(async (credentials: LoginRequest): Promise<SessionUser> => {
    const session = await login(credentials);
    return session.user;
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await logout();
    } catch {
      // `logout` clears local state in its own `finally`, so a failed round trip
      // still signs the user out of this browser.
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status: state.status, user: state.user, signIn, signOut }),
    [state.status, state.user, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
